import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import useThemeColors from "../../hooks/useThemeColors";

const Privacy: React.FC = () => {
  const colors = useThemeColors();

  return (
    <Container maxW="4xl" py={8}>
      <VStack gap={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" color={colors.text.primary} mb={4}>
            Privacy Policy
          </Heading>
          <Text fontSize="lg" color={colors.text.secondary}>
            Last updated: Last updated: 13/08/2025
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            1. Introduction
          </Heading>
          <Text mb={4}>
            At Result.ng, we are committed to protecting the privacy and
            security of your personal information. This Privacy Policy explains
            how we collect, use, disclose, and safeguard your information when
            you use our school management system.
          </Text>
          <Text>
            This policy applies to all users of Result.ng, including school
            administrators, teachers, staff, students, and parents/guardians.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            2. Information We Collect
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              <strong>School Information:</strong> We collect information about
              your school, including school name, address, phone number, email,
              and website.
            </Text>
            <Text>
              <strong>User Account Information:</strong> When you register, we
              collect your name, email address, password, and role within the
              school.
            </Text>
            <Text>
              <strong>Student Information:</strong> We collect student data
              including names, dates of birth, gender, academic records, class
              assignments, and performance data.
            </Text>
            <Text>
              <strong>Guardian Information:</strong> We collect parent/guardian
              contact information, including names, phone numbers, email
              addresses, and addresses.
            </Text>
            <Text>
              <strong>Academic Data:</strong> We collect grades, assessments,
              attendance records, behavioral notes, and other educational
              information.
            </Text>
            <Text>
              <strong>Usage Data:</strong> We collect information about how you
              use the Service, including login times, features accessed, and
              system interactions.
            </Text>
            <Text>
              <strong>Technical Data:</strong> We collect device information, IP
              addresses, browser type, and other technical data for security and
              performance purposes.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            3. How We Use Your Information
          </Heading>
          <Text mb={4}>
            We use the collected information for the following purposes:
          </Text>
          <VStack gap={3} mb={4} align="start">
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Service Provision:</strong> To provide and maintain the
                school management system
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Academic Management:</strong> To manage student records,
                grades, and academic progress
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Communication:</strong> To send notifications,
                announcements, and important updates
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Analytics:</strong> To generate reports and analyze
                school performance
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Security:</strong> To protect against fraud, abuse, and
                unauthorized access
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Compliance:</strong> To comply with legal obligations
                and educational regulations
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Improvement:</strong> To improve our services and
                develop new features
              </Text>
            </HStack>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            4. Educational Data Protection
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We are committed to protecting student data in accordance with
              applicable educational privacy laws and regulations.
            </Text>
            <Text>
              <strong>FERPA Compliance:</strong> We comply with the Family
              Educational Rights and Privacy Act (FERPA) and ensure that student
              records are protected and only accessible to authorized personnel.
            </Text>
            <Text>
              <strong>Parental Rights:</strong> Parents and guardians have the
              right to access their children's educational records and request
              corrections as permitted by law.
            </Text>
            <Text>
              <strong>Student Privacy:</strong> We implement strict access
              controls to ensure that student information is only accessible to
              authorized school personnel.
            </Text>
            <Text>
              <strong>Data Minimization:</strong> We collect only the minimum
              amount of student data necessary to provide educational services.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            5. Information Sharing and Disclosure
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We do not sell, trade, or otherwise transfer your personal
              information to third parties without your consent, except in the
              following circumstances:
            </Text>
            <Text>
              <strong>Service Providers:</strong> We may share information with
              trusted third-party service providers who assist us in operating
              the Service, such as cloud hosting providers and email services.
            </Text>
            <Text>
              <strong>Legal Requirements:</strong> We may disclose information
              when required by law, court order, or government request.
            </Text>
            <Text>
              <strong>Safety and Security:</strong> We may share information to
              protect the safety and security of our users and the Service.
            </Text>
            <Text>
              <strong>Business Transfers:</strong> In the event of a merger,
              acquisition, or sale of assets, user information may be
              transferred as part of the transaction.
            </Text>
            <Text>
              <strong>Consent:</strong> We may share information with your
              explicit consent for specific purposes.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            6. Data Security
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We implement comprehensive security measures to protect your
              information:
            </Text>
            <VStack gap={2} mb={4} align="start">
              <Text>• Encryption of data in transit and at rest</Text>
              <Text>• Secure servers with access controls</Text>
              <Text>• Regular security audits and updates</Text>
              <Text>• Multi-factor authentication for user accounts</Text>
              <Text>• Role-based access controls</Text>
              <Text>• Regular backups and disaster recovery procedures</Text>
            </VStack>
            <Text>
              While we strive to protect your information, no method of
              transmission over the internet or electronic storage is 100%
              secure. We cannot guarantee absolute security.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            7. Data Retention
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We retain your information for as long as necessary to provide the
              Service and comply with legal obligations:
            </Text>
            <Text>
              <strong>Active Accounts:</strong> We retain information for active
              user accounts and as long as the account remains active.
            </Text>
            <Text>
              <strong>Educational Records:</strong> Student records are retained
              in accordance with educational data retention requirements and
              school policies.
            </Text>
            <Text>
              <strong>Legal Requirements:</strong> We may retain certain
              information to comply with legal obligations, resolve disputes,
              and enforce agreements.
            </Text>
            <Text>
              <strong>Deletion Requests:</strong> You may request deletion of
              your information, subject to legal and educational requirements.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            8. Your Rights and Choices
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              You have the following rights regarding your personal information:
            </Text>
            <VStack gap={2} mb={4} align="start">
              <Text>
                • <strong>Access:</strong> Request access to your personal
                information
              </Text>
              <Text>
                • <strong>Correction:</strong> Request correction of inaccurate
                information
              </Text>
              <Text>
                • <strong>Deletion:</strong> Request deletion of your
                information (subject to legal requirements)
              </Text>
              <Text>
                • <strong>Portability:</strong> Request a copy of your data in a
                portable format
              </Text>
              <Text>
                • <strong>Objection:</strong> Object to certain processing of
                your information
              </Text>
              <Text>
                • <strong>Withdrawal:</strong> Withdraw consent for data
                processing where applicable
              </Text>
            </VStack>
            <Text>
              To exercise these rights, please contact us using the information
              provided at the end of this policy.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            9. Cookies and Tracking Technologies
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We use cookies and similar tracking technologies to enhance your
              experience and provide certain functionality:
            </Text>
            <Text>
              <strong>Essential Cookies:</strong> Required for basic
              functionality and security
            </Text>
            <Text>
              <strong>Performance Cookies:</strong> Help us understand how users
              interact with the Service
            </Text>
            <Text>
              <strong>Functional Cookies:</strong> Remember your preferences and
              settings
            </Text>
            <Text>
              You can control cookie settings through your browser preferences,
              though disabling certain cookies may affect Service functionality.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            10. International Data Transfers
          </Heading>
          <Text>
            Your information may be transferred to and processed in countries
            other than your own. We ensure that such transfers comply with
            applicable data protection laws and implement appropriate safeguards
            to protect your information.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            11. Children's Privacy
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We are committed to protecting the privacy of children and comply
              with applicable laws regarding children's data protection.
            </Text>
            <Text>
              <strong>Parental Consent:</strong> We require parental consent for
              the collection of personal information from children under 13 (or
              the applicable age in your jurisdiction).
            </Text>
            <Text>
              <strong>Educational Context:</strong> In educational settings,
              student data collection is governed by educational privacy laws
              and school policies.
            </Text>
            <Text>
              <strong>Limited Collection:</strong> We collect only the minimum
              amount of information necessary for educational purposes.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            12. Changes to This Privacy Policy
          </Heading>
          <Text>
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes by posting the new policy on the Service
            and updating the "Last updated" date. Your continued use of the
            Service after such changes constitutes acceptance of the new policy.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            13. Contact Us
          </Heading>
          <Text mb={4}>
            If you have any questions about this Privacy Policy or our data
            practices, please contact us:
          </Text>
          <VStack gap={2} align="start">
            <Text>Email: privacy@result.ng</Text>
          </VStack>
        </Box>

        <Box
          borderTop="1px solid"
          borderColor="gray.200"
          pt={4}
          textAlign="center"
        >
          <Text fontSize="sm" color={colors.text.secondary}>
            By using Result.ng, you acknowledge that you have read and
            understood this Privacy Policy and consent to the collection, use,
            and disclosure of your information as described herein.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default Privacy;
